import { get } from 'es-toolkit/compat'
import { connect } from 'react-redux'
import React from 'react'
import useObjFromList from 'core/hooks/useObjFromList'
import { useSportsForStore } from 'core/hooks/useSports'
import type { AppStore } from 'core/redux/store'
import { comparing } from 'core/utils/sort'
import type { Sport } from 'core/models/api/sports-piloting/sports-piloting'
import { Combobox } from '../mygame/combobox/combobox'
import { processComboboxChange, type SelectAllOption, selectAllOption } from 'core/utils/combobox'
import { useTranslation } from 'react-i18next'

export interface IProps {
  id?: string
  label?: string
  className?: string
  value?: number | number[]
  isDisabled?: boolean
  hasBottomLine?: boolean
  sportSortFunction?: (a: Sport, b: Sport) => number
  onChange?: (value: number | number[]) => void
  disabledItemsIds?: number[]
  isMultiple?: boolean
  initialIsOpen?: boolean
  canBeReset?: boolean
  storeId?: number
}

export interface IDumbProps extends IProps {
  sports: Sport[]
  isLoading?: boolean
}

const disableListItem = (list: any, disabledItemsIds: number[]): any[] => {
  return list.map(item => ({
    ...item,
    isDisabled: disabledItemsIds?.includes(item.id) || false
  }))
}

export const DumbSelectSport = ({
  id,
  sports = [],
  disabledItemsIds,
  value,
  isLoading,
  isDisabled,
  onChange,
  isMultiple,
  canBeReset
}: IDumbProps) => {
  const { t } = useTranslation()
  const sportsSorted = [...sports].sort(comparing<Sport>(it => it.department_id))
  const sportsWithDepartmentIdInLabel = sportsSorted.map(sport => ({
    ...sport,
    label: `${sport.department_id ? `${sport.department_id} - ` : ''}${sport.label}`
  }))
  const sportsWithDisabledState = disableListItem
    ? disableListItem(sportsWithDepartmentIdInLabel, disabledItemsIds)
    : sportsWithDepartmentIdInLabel

  const optionsList: (SelectAllOption | Sport)[] = isMultiple
    ? [selectAllOption(), ...sportsWithDisabledState]
    : [...sportsWithDisabledState]

  const objFromList = useObjFromList(sportsWithDisabledState, 'id')
  const sportSelected: (SelectAllOption | Sport)[] | Sport =
    isMultiple && Array.isArray(value)
      ? (optionsList.filter(sport => value.includes(sport.id)) ?? null)
      : (objFromList[value as number] ?? null)

  const placeholder = isLoading && !sports.length ? t('loading') : t('settings.select_sport')

  const handleChangeCombobox = (value, actionMeta) => {
    processComboboxChange(value, actionMeta, onChange, optionsList, disabledItemsIds)
  }

  return (
    <Combobox<Sport | SelectAllOption>
      value={sportSelected}
      onChange={handleChangeCombobox}
      comboboxLabel={t('sport')}
      options={optionsList}
      testId={id}
      isMulti={isMultiple}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isClearable={canBeReset}
    />
  )
}

export const SelectSport: React.FunctionComponent<IProps> = props => {
  const { storeId } = props
  const { data: sports, isLoading } = useSportsForStore(storeId)
  return (
    <>
      {storeId ? (
        <DumbSelectSport sports={sports} {...props} isLoading={isLoading} />
      ) : (
        <DumbSelectSport sports={[]} {...props} />
      )}
    </>
  )
}

const mapStateToProps = (state: AppStore, { value, storeId }: IProps) => ({
  storeId: storeId !== undefined ? storeId : get(state, 'filters.store_id'),
  value: value !== undefined ? value : get(state, 'filters.sport')
})

export default connect(mapStateToProps)(SelectSport)
